// Generat automat din README.md - Phase 3: Enterprise Overview
import React from 'react';
import { 
  Sparkles,
  TrendingUp, 
  BarChart3,
  Zap,
  Database,
  Shield,
  Users,
  Globe,
  Clock,
  Target,
  Activity,
  CheckCircle,
  ArrowRight,
  Star
} from 'lucide-react';
import { Link } from 'react-router-dom';
import Card from '../../components/Card';

const EnterpriseOverview = () => {
  const enterpriseFeatures = [
    {
      id: 'dashboard',
      title: 'Executive Dashboard',
      description: 'Real-time business intelligence și strategic insights cu predictive analytics',
      icon: TrendingUp,
      path: '/enterprise/dashboard',
      status: 'ready',
      complexity: 'High',
      benefits: ['Real-time KPIs', 'Business Intelligence', 'Predictive Analytics', 'Strategic Insights']
    },
    {
      id: 'reporting',
      title: 'Advanced Reporting Engine',
      description: 'Custom reports cu AI-powered insights și automated delivery',
      icon: BarChart3,
      path: '/enterprise/reporting',
      status: 'ready',
      complexity: 'High',
      benefits: ['Custom Reports', 'AI Insights', 'Automated Delivery', 'Multi-format Export']
    },
    {
      id: 'workflows',
      title: 'Workflow Automation Engine',
      description: 'Visual workflow builder cu automated triggers și advanced logic',
      icon: Zap,
      path: '/enterprise/workflows',
      status: 'ready',
      complexity: 'Medium',
      benefits: ['Visual Builder', 'Automated Triggers', 'Advanced Logic', 'Time Savings']
    },
    {
      id: 'database',
      title: 'Multi-Database Connector',
      description: 'Enterprise integration cu Oracle, SAP, MongoDB, PostgreSQL în real-time',
      icon: Database,
      path: '/enterprise/database',
      status: 'planned',
      complexity: 'High',
      benefits: ['Multi-DB Support', 'Real-time Sync', 'Enterprise Integration', 'Data Consistency']
    }
  ];

  const stats = {
    automationHours: '2,340',
    reportsGenerated: '1,247',
    workflowsActive: '23',
    dataSources: '12'
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'ready': return 'text-green-600 bg-green-50 border-green-200';
      case 'development': return 'text-blue-600 bg-blue-50 border-blue-200';
      case 'planned': return 'text-gray-600 bg-gray-50 border-gray-200';
      default: return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const getComplexityColor = (complexity) => {
    switch(complexity) {
      case 'High': return 'text-red-600';
      case 'Medium': return 'text-yellow-600';
      case 'Low': return 'text-green-600';
      default: return 'text-gray-600';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl p-8">
        <Sparkles className="w-16 h-16 mx-auto mb-4 opacity-90" />
        <h1 className="text-4xl font-bold mb-2">Enterprise Suite</h1>
        <p className="text-xl opacity-90 mb-6">
          Advanced features pentru enterprise-level automation și business intelligence
        </p>
        <div className="flex justify-center space-x-8 text-sm">
          <div className="text-center">
            <div className="text-2xl font-bold">{stats.automationHours}</div>
            <div className="opacity-75">Hours Automated</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold">{stats.reportsGenerated}</div>
            <div className="opacity-75">Reports Generated</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold">{stats.workflowsActive}</div>
            <div className="opacity-75">Active Workflows</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold">{stats.dataSources}</div>
            <div className="opacity-75">Data Sources</div>
          </div>
        </div>
      </div>

      {/* Enterprise Features Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {enterpriseFeatures.map((feature) => {
          const Icon = feature.icon;
          const isReady = feature.status === 'ready';
          
          return (
            <Card key={feature.id} className={`hover:shadow-lg transition-all ${isReady ? 'hover:scale-105' : ''}`}>
              <div className="flex items-start space-x-4">
                <div className={`p-3 rounded-xl ${isReady ? 'bg-blue-100 dark:bg-blue-900/30' : 'bg-gray-100 dark:bg-gray-700'}`}>
                  <Icon className={`w-8 h-8 ${isReady ? 'text-blue-600' : 'text-gray-500'}`} />
                </div>
                
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                      {feature.title}
                    </h3>
                    <div className="flex items-center space-x-2">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full border ${getStatusColor(feature.status)}`}>
                        {feature.status}
                      </span>
                      <span className={`text-sm font-medium ${getComplexityColor(feature.complexity)}`}>
                        {feature.complexity}
                      </span>
                    </div>
                  </div>
                  
                  <p className="text-gray-600 dark:text-gray-400 mb-4">
                    {feature.description}
                  </p>
                  
                  {/* Benefits */}
                  <div className="mb-4">
                    <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2">Key Benefits:</h4>
                    <div className="flex flex-wrap gap-1">
                      {feature.benefits.map((benefit) => (
                        <span key={benefit} className="px-2 py-1 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 text-xs rounded">
                          {benefit}
                        </span>
                      ))}
                    </div>
                  </div>
                  
                  {/* Action Button */}
                  {isReady ? (
                    <Link
                      to={feature.path}
                      className="inline-flex items-center px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors space-x-2"
                    >
                      <span>Launch</span>
                      <ArrowRight className="w-4 h-4" />
                    </Link>
                  ) : (
                    <div className="inline-flex items-center px-4 py-2 bg-gray-300 text-gray-500 rounded-lg cursor-not-allowed space-x-2">
                      <span>Coming Soon</span>
                      <Clock className="w-4 h-4" />
                    </div>
                  )}
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      {/* Enterprise Benefits */}
      <Card title="Why Enterprise Suite?" icon={Star}>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center">
            <div className="p-4 bg-green-100 dark:bg-green-900/30 rounded-full inline-block mb-4">
              <Activity className="w-8 h-8 text-green-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Automation & Efficiency</h3>
            <p className="text-gray-600 dark:text-gray-400 text-sm">
              Automatizează procese complexe și reduce timpul manual cu până la 80%
            </p>
          </div>
          
          <div className="text-center">
            <div className="p-4 bg-blue-100 dark:bg-blue-900/30 rounded-full inline-block mb-4">
              <BarChart3 className="w-8 h-8 text-blue-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Advanced Analytics</h3>
            <p className="text-gray-600 dark:text-gray-400 text-sm">
              Insights profunde cu AI și machine learning pentru decizii informate
            </p>
          </div>
          
          <div className="text-center">
            <div className="p-4 bg-purple-100 dark:bg-purple-900/30 rounded-full inline-block mb-4">
              <Shield className="w-8 h-8 text-purple-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Enterprise Security</h3>
            <p className="text-gray-600 dark:text-gray-400 text-sm">
              Securitate la nivel enterprise cu audit trails și compliance GDPR
            </p>
          </div>
        </div>
      </Card>

      {/* Implementation Roadmap */}
      <Card title="Implementation Roadmap" icon={Target}>
        <div className="space-y-4">
          <div className="flex items-center space-x-4 p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border-l-4 border-green-500">
            <CheckCircle className="w-6 h-6 text-green-500" />
            <div>
              <h4 className="font-semibold text-gray-900 dark:text-white">Phase 3.1 - Core Features (COMPLETED)</h4>
              <p className="text-sm text-gray-600 dark:text-gray-400">Executive Dashboard, Advanced Reporting, Workflow Automation</p>
            </div>
          </div>
          
          <div className="flex items-center space-x-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border-l-4 border-blue-500">
            <Clock className="w-6 h-6 text-blue-500" />
            <div>
              <h4 className="font-semibold text-gray-900 dark:text-white">Phase 3.2 - Integration Layer (IN PROGRESS)</h4>
              <p className="text-sm text-gray-600 dark:text-gray-400">Multi-Database Connector, API Management Suite, Advanced Security</p>
            </div>
          </div>
          
          <div className="flex items-center space-x-4 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg border-l-4 border-gray-300">
            <Globe className="w-6 h-6 text-gray-500" />
            <div>
              <h4 className="font-semibold text-gray-900 dark:text-white">Phase 3.3 - Global Features (PLANNED)</h4>
              <p className="text-sm text-gray-600 dark:text-gray-400">Multi-language Support, Performance Optimization, Mobile Architecture</p>
            </div>
          </div>
        </div>
      </Card>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Link 
          to="/enterprise/dashboard"
          className="p-6 bg-gradient-to-br from-blue-500 to-blue-600 text-white rounded-xl hover:from-blue-600 hover:to-blue-700 transition-all transform hover:scale-105"
        >
          <TrendingUp className="w-8 h-8 mb-3" />
          <h3 className="font-semibold mb-1">View Executive Dashboard</h3>
          <p className="text-sm opacity-90">Real-time business metrics și KPIs</p>
        </Link>
        
        <Link 
          to="/enterprise/reporting"
          className="p-6 bg-gradient-to-br from-purple-500 to-purple-600 text-white rounded-xl hover:from-purple-600 hover:to-purple-700 transition-all transform hover:scale-105"
        >
          <BarChart3 className="w-8 h-8 mb-3" />
          <h3 className="font-semibold mb-1">Generate Reports</h3>
          <p className="text-sm opacity-90">Custom reports cu AI insights</p>
        </Link>
        
        <Link 
          to="/enterprise/workflows"
          className="p-6 bg-gradient-to-br from-yellow-500 to-yellow-600 text-white rounded-xl hover:from-yellow-600 hover:to-yellow-700 transition-all transform hover:scale-105"
        >
          <Zap className="w-8 h-8 mb-3" />
          <h3 className="font-semibold mb-1">Create Workflows</h3>
          <p className="text-sm opacity-90">Automatizează procesele business</p>
        </Link>
      </div>

      {/* Support Information */}
      <Card>
        <div className="text-center p-6">
          <Users className="w-12 h-12 text-blue-500 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
            Enterprise Support Available
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            Echipa noastră de specialiști enterprise este disponibilă pentru implementare și suport tehnic 24/7
          </p>
          <div className="flex justify-center space-x-4">
            <div className="text-center">
              <div className="text-lg font-bold text-blue-600">24/7</div>
              <div className="text-sm text-gray-600">Technical Support</div>
            </div>
            <div className="text-center">
              <div className="text-lg font-bold text-green-600">99.9%</div>
              <div className="text-sm text-gray-600">Uptime SLA</div>
            </div>
            <div className="text-center">
              <div className="text-lg font-bold text-purple-600">Custom</div>
              <div className="text-sm text-gray-600">Implementation</div>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default EnterpriseOverview;